/* Display formatting for dates and times — one style across the app:
 *   fmtDate      -> "07 Oct 26"
 *   fmtTime      -> "05:13:29 PM"
 *   fmtDateTime  -> "07 Oct 26, 05:13:29 PM"
 *
 * For display only. Dates sent to the API keep their own formats
 * (convertUTCDate / formatDate in the callers) and must not use these.
 *
 * Accepts what the backends send:
 *   "2026-10-07", "2026-10-07T17:13:29.123", "2026-10-07 17:13:29", and
 *   [2026, 10, 7, 17, 13, 29] — order-service report DTOs serialise
 *   LocalDateTime as an array whose month is 1-based.
 *
 * `zone` keeps each screen's existing reading of the value; only the look changes:
 *   undefined   — shown as stored (IMS, and the screens that used `new Date(...)`)
 *   "utc-local" — stored in UTC, shown in the browser's zone (was convertUTCDate)
 *   "utc-ist"   — stored in UTC, shown in IST (was fixMonthDisplay)
 *
 * A value that cannot be read is returned as it came (never "Invalid date"). */
import moment from "moment";

const PARSE_FORMATS = [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD"];
const DATE_FMT = "DD MMM YY";
const TIME_FMT = "hh:mm:ss A";

const isDateOnly = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim());

const fromArray = (a, utc) => {
  const [year, month, day, hour = 0, minute = 0, second = 0] = a;
  const parts = { year, month: Number(month) - 1, date: day, hour, minute, second };
  return utc ? moment.utc(parts) : moment(parts);
};

const toMoment = (v, zone) => {
  if (v == null || v === "") return null;
  const utc = zone === "utc-local" || zone === "utc-ist";
  let m;
  if (Array.isArray(v)) m = fromArray(v, utc);
  else if (v instanceof Date) m = moment(v);
  else m = utc ? moment.utc(String(v).trim(), PARSE_FORMATS, true) : moment(String(v).trim(), PARSE_FORMATS, true);
  if (!m.isValid()) return null;
  if (zone === "utc-local") return m.local();
  if (zone === "utc-ist") return m.utcOffset("+05:30");
  return m;
};

const fallback = (v) => (v == null || Array.isArray(v) ? "" : String(v));

export const fmtDate = (v, { zone } = {}) => {
  /* A plain calendar date has no zone to convert. */
  const m = isDateOnly(v) ? toMoment(v) : toMoment(v, zone);
  return m ? m.format(DATE_FMT) : fallback(v);
};

/* "" for a date-only value — there is no time to show. */
export const fmtTime = (v, { zone } = {}) => {
  if (v == null || v === "" || isDateOnly(v)) return "";
  const m = toMoment(v, zone);
  return m ? m.format(TIME_FMT) : "";
};

export const fmtDateTime = (v, { zone } = {}) => {
  if (isDateOnly(v)) return fmtDate(v);
  const m = toMoment(v, zone);
  return m ? `${m.format(DATE_FMT)}, ${m.format(TIME_FMT)}` : fallback(v);
};
